import { useId, useState } from 'react'

/**
 * Le champ où l'on tape un code à quatre chiffres.
 *
 * Un seul champ, et non quatre cases séparées : le collage, l'effacement arrière et le clavier
 * du téléphone s'y comportent comme partout ailleurs, là où quatre cases demandent de recoder
 * chacun de ces gestes. Les chiffres sont espacés pour qu'on les compte d'un coup d'œil.
 *
 * `inputMode="numeric"` fait monter le pavé numérique sur un téléphone — avec le clavier
 * complet, taper quatre chiffres demande un détour par la touche de bascule.
 */
export function ChampCode({
  libelle,
  valeur,
  onChange,
  onEntree,
  autoFocus = false,
  enErreur = false,
}: {
  libelle: string
  valeur: string
  onChange: (valeur: string) => void
  /** Appelé sur « Entrée » : sur un code de quatre chiffres, c'est le geste qui vient seul. */
  onEntree?: () => void
  autoFocus?: boolean
  enErreur?: boolean
}) {
  const identifiant = useId()
  const [montre, setMontre] = useState(false)

  return (
    <>
      <div className="flex items-baseline justify-between">
        <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor={identifiant}>
          {libelle}
        </label>
        {/* Aucun code ne se récupère : afficher ce qu'on tape évite de s'enfermer dehors sur une
            faute de frappe. Même raison que pour le mot de passe. */}
        <button
          type="button"
          onClick={() => setMontre((visible) => !visible)}
          className="text-meta font-semibold text-accent"
        >
          {montre ? 'Masquer' : 'Afficher'}
        </button>
      </div>

      <input
        id={identifiant}
        type={montre ? 'text' : 'password'}
        inputMode="numeric"
        autoComplete="off"
        // Quatre chiffres, et rien d'autre : le filtre est ici plutôt que dans un message
        // d'erreur, pour qu'une lettre tapée par mégarde ne s'affiche jamais.
        value={valeur}
        onChange={(evenement) => onChange(evenement.target.value.replace(/\D/g, '').slice(0, 4))}
        onKeyDown={(evenement) => {
          if (evenement.key === 'Enter' && onEntree) {
            onEntree()
          }
        }}
        // Le focus d'emblée est justifié ici : l'écran ne demande que ce code, et rien d'autre
        // n'y est à faire. Laissé au choix de l'appelant, qui sait s'il est seul à l'écran.
        autoFocus={autoFocus}
        aria-invalid={enErreur}
        className="mt-2 w-full rounded-field border-[1.5px] bg-card px-4 text-center outline-none"
        style={{
          height: 56,
          fontSize: 28,
          letterSpacing: '0.5em',
          // Le décalage compense l'espacement, qui s'ajoute aussi après le dernier chiffre et
          // ferait paraître la série collée à gauche.
          textIndent: '0.5em',
          borderColor: enErreur ? 'var(--color-danger)' : 'var(--color-accent)',
        }}
      />
    </>
  )
}
